import type { ApiClient } from './ApiClient';
import type { SchoolPaymentMethodType } from './schoolPaymentMethod';

// PAY-16 — formes alignées sur apps/api/src/routes/paymentProofs.ts et
// apps/api/src/lib/paymentProof.ts (seule source de vérité). Dépôt +
// validation/rejet d'une preuve de paiement — pure traçabilité, JAMAIS un
// impact sur Invoice/Payment (voir ces deux fichiers pour la règle métier
// complète).

export type PaymentProofStatus = 'PENDING' | 'VALIDATED' | 'REJECTED';
export type PaymentProofDecision = 'VALIDATED' | 'REJECTED';

export interface PaymentProof {
  id: string;
  studentId: string;
  status: PaymentProofStatus;
  originalFilename: string;
  mimeType: string;
  fileSize: number;
  note: string | null;
  claimedDestinationType: SchoolPaymentMethodType | null;
  submittedAt: string;
  reviewedAt: string | null;
  rejectionReason: string | null;
}

/** Renvoyée UNIQUEMENT par `GET /schools/:id/payment-proofs` (audience école). */
export interface PaymentProofWithStudent extends PaymentProof {
  student: { id: string; firstName: string; lastName: string };
}

/**
 * Dépose une preuve de paiement pour SON enfant (`requireParentOwnsStudent`,
 * AUT-04). `note`/`claimedDestinationType` optionnels — envoyés en
 * `multipart/form-data` avec `file` (`ApiClient#post` détecte un corps
 * `FormData` et laisse le navigateur poser lui-même `Content-Type`/le
 * boundary, voir ApiClient.ts).
 */
export function submitPaymentProof(
  client: ApiClient,
  studentId: string,
  file: File,
  accessToken: string,
  options?: { note?: string; claimedDestinationType?: SchoolPaymentMethodType },
) {
  const formData = new FormData();
  formData.append('file', file);
  if (options?.note) {
    formData.append('note', options.note);
  }
  if (options?.claimedDestinationType) {
    formData.append('claimedDestinationType', options.claimedDestinationType);
  }
  return client.post<{ paymentProof: PaymentProof }>(`/parents/students/${studentId}/payment-proofs`, formData, {
    accessToken,
  });
}

/** Parent — toutes ses preuves déposées pour cet enfant, quel que soit le statut. */
export function listOwnPaymentProofs(client: ApiClient, studentId: string, accessToken: string) {
  return client.get<{ paymentProofs: PaymentProof[] }>(`/parents/students/${studentId}/payment-proofs`, {
    accessToken,
  });
}

/**
 * École — les preuves déposées pour ses élèves. `status` optionnel (défaut
 * serveur `PENDING`, même convention que `GET /enrollments/review`).
 */
export function listSchoolPaymentProofs(
  client: ApiClient,
  schoolId: string,
  accessToken: string,
  status?: PaymentProofStatus,
) {
  const query = status ? `?status=${status}` : '';
  return client.get<{ paymentProofs: PaymentProofWithStudent[] }>(`/schools/${schoolId}/payment-proofs${query}`, {
    accessToken,
  });
}

/**
 * Valide ou rejette une preuve — `reason` optionnel même sur un rejet
 * (divergence délibérée avec ENR-07, voir routes/paymentProofs.ts). Ne
 * touche JAMAIS `Invoice`/`Payment`.
 */
export function decidePaymentProof(
  client: ApiClient,
  proofId: string,
  decision: PaymentProofDecision,
  accessToken: string,
  reason?: string,
) {
  return client.post<{ paymentProof: PaymentProof }>(
    `/payment-proofs/${proofId}/decision`,
    { decision, ...(reason ? { reason } : {}) },
    { accessToken },
  );
}

/**
 * Récupère le contenu binaire de la preuve (`Blob`) — route PARTAGÉE entre
 * le parent propriétaire et l'école du tenant concerné. Le jeton reste dans
 * l'en-tête `Authorization` (`ApiClient#getBlob`, jamais en paramètre
 * d'URL) — l'appelant convertit en URL d'objet local
 * (`URL.createObjectURL`).
 */
export function fetchPaymentProofFile(client: ApiClient, proofId: string, accessToken: string): Promise<Blob> {
  return client.getBlob(`/payment-proofs/${proofId}/file`, { accessToken });
}
